// src/components/upmode-agent/agent-manager-modal.tsx
import React, { useState, useEffect, useMemo } from "react";
import {
  X, Plus, Save, Trash2, Bot, AlertCircle, Edit3,
  Wrench, Database, Mail, Globe, Sparkles,
  Rocket, FileCode2, Linkedin, Globe as GlobeWeb,
} from "lucide-react";
import { toast } from "sonner";
import {
  upmodeAgentApi, UpmodeAgent, ExampleConversation, PublishDestination,
} from "@/app/services/upmode-agent/upmode-agent-api";
import {
  publishingApi, Integration as PublishingIntegration,
} from "@/app/services/publishing/publishing-api";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/alert";
import { ConfirmDialog } from "../ui/confirm-dialog";

const MODEL_OPTIONS = [
  { id: "claude-haiku-4-5",  label: "Claude Haiku 4.5",   tag: "Fast · recommended" },
  { id: "claude-sonnet-4-5", label: "Claude Sonnet 4.5",  tag: "Balanced" },
  { id: "claude-opus-4-7",   label: "Claude Opus 4.7",    tag: "Most capable" },
];

const DEFAULT_AGENT: Omit<UpmodeAgent, "id" | "username" | "is_preset" | "is_active" | "created_at" | "updated_at"> = {
  name: "",
  description: "",
  avatar_emoji: "🤖",
  system_prompt: "You are a helpful assistant.",
  model: "claude-haiku-4-5",
  temperature: 0.7,
  tools_enabled: { read_brand_data: true, send_email: false, web_search: false },
  example_conversations: [],
  brand_name: null,
  publish_destinations: [],
};

const DEST_META: Record<PublishDestination, { label: string; color: string; Icon: React.ComponentType<{ style?: React.CSSProperties }> }> = {
  hashnode:  { label: "Hashnode",  color: "#2962FF", Icon: FileCode2 },
  wordpress: { label: "WordPress", color: "#21759B", Icon: GlobeWeb },
  linkedin:  { label: "LinkedIn",  color: "#0A66C2", Icon: Linkedin },
};

interface AgentManagerModalProps {
  agents: UpmodeAgent[];
  activeBrandName?: string;
  onClose: () => void;
  onChanged: () => void;
}

export function AgentManagerModal({ agents, activeBrandName, onClose, onChanged }: AgentManagerModalProps) {
  const [editing, setEditing] = useState<UpmodeAgent | null>(null);
  const [creating, setCreating] = useState<typeof DEFAULT_AGENT | null>(null);
  const [agentPendingDelete, setAgentPendingDelete] = useState<UpmodeAgent | null>(null);

  // Load configured publishing integrations for the active brand.
  // We hoist this to the modal root so the editor form gets it as a prop.
  const [publishingIntegrations, setPublishingIntegrations] = useState<PublishingIntegration[]>([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoadingIntegrations(true);
    (async () => {
      try {
        const list = await publishingApi.listIntegrations(activeBrandName);
        if (!cancelled) setPublishingIntegrations(list);
      } catch {
        if (!cancelled) setPublishingIntegrations([]);
      } finally {
        if (!cancelled) setLoadingIntegrations(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeBrandName]);

  const requestDelete = (a: UpmodeAgent) => setAgentPendingDelete(a);

  const confirmDelete = async () => {
    const a = agentPendingDelete;
    if (!a) return;
    try {
      await upmodeAgentApi.deleteAgent(a.id);
      toast.success("Agent deleted");
      onChanged();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setAgentPendingDelete(null);
    }
  };

  const current = editing || creating;

  return (
    <div
      className="p-3 md:p-5"
      style={{
        // z-index 40: intentionally below Radix dialog portals (default z-50)
        // so the "Delete agent" ConfirmDialog renders above this modal.
        // The backdrop still covers the main page (which has no z-index).
        position: "fixed", inset: 0, zIndex: 40,
        backgroundColor: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "min(920px, calc(100dvw - 24px))",
          maxHeight: "min(92dvh, calc(100dvh - 24px))",
          borderRadius: 16,
          backgroundColor: "var(--background)",
          border: "1px solid var(--border)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 30px 60px rgba(0,0,0,0.4)",
        }}
      >
        {/* Header */}
        <div
          className="px-4 md:px-6 py-3 md:py-4"
          style={{
            borderBottom: "1px solid var(--border)",
            display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            flexShrink: 0,
          }}
        >
          <Bot style={{ width: 18, height: 18, color: "#0D9488", flexShrink: 0 }} />
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, overflowWrap: "anywhere", wordBreak: "break-word" }}>
            {current ? (editing ? "Edit Agent" : "Create New Agent") : "Manage Agents"}
          </h3>
          {!current && activeBrandName && (
            <span style={{
              fontSize: 10, fontWeight: 700, color: "#0D9488",
              backgroundColor: "rgba(13,148,136,0.08)",
              border: "1px solid rgba(13,148,136,0.2)",
              padding: "2px 8px", borderRadius: 999,
              textTransform: "uppercase", letterSpacing: "0.05em",
              overflowWrap: "anywhere", wordBreak: "break-word", maxWidth: "100%",
            }}>
              Brand: {activeBrandName}
            </span>
          )}
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ marginLeft: "auto", padding: 6, borderRadius: 7, border: "none", backgroundColor: "transparent", cursor: "pointer", color: "var(--muted-foreground)", flexShrink: 0 }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <div style={{ flex: 1, overflow: "auto", minHeight: 0 }}>
          {!current ? (
            // ── Agent List ──────────────────────────────────────────────
            <div className="p-4 md:p-5">
              <div style={{
                marginBottom: 14, padding: "10px 12px", borderRadius: 10,
                backgroundColor: "rgba(13,148,136,0.05)",
                border: "1px solid rgba(13,148,136,0.15)",
                fontSize: 11, color: "var(--foreground)", lineHeight: 1.5,
                overflowWrap: "anywhere", wordBreak: "break-word",
              }}>
                <strong style={{ color: "#0D9488" }}>How agents work:</strong>{" "}
                Agents reason over your Upmode brand data (monitoring, cognition, fix actions, simulations, prompt tracking). Enable <strong>Web search</strong> to let an agent reason over the live web too, or select <strong>publish destinations</strong> to let it draft content you approve before publishing.
              </div>

              <button
                onClick={() => setCreating({ ...DEFAULT_AGENT, brand_name: activeBrandName ?? null })}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 11, border: "2px dashed rgba(13,148,136,0.3)", backgroundColor: "rgba(13,148,136,0.04)", cursor: "pointer", marginBottom: 14, textAlign: "left" }}
              >
                <div style={{ width: 32, height: 32, borderRadius: 9, backgroundColor: "rgba(13,148,136,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Plus style={{ width: 15, height: 15, color: "#0D9488" }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#0D9488" }}>Create New Agent</div>
                  <div style={{ fontSize: 11, color: "var(--muted-foreground)", overflowWrap: "anywhere", wordBreak: "break-word" }}>
                    {activeBrandName
                      ? <>Will be scoped to <strong>{activeBrandName}</strong></>
                      : "No active brand — will be available across all brands"}
                  </div>
                </div>
              </button>

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {agents.map(a => (
                  <AgentListRow
                    key={a.id}
                    agent={a}
                    onEdit={() => setEditing(a)}
                    onDelete={() => requestDelete(a)}
                  />
                ))}
              </div>
            </div>
          ) : (
            <AgentEditor
              initial={current}
              isEdit={!!editing}
              activeBrandName={activeBrandName}
              publishingIntegrations={publishingIntegrations}
              loadingIntegrations={loadingIntegrations}
              onCancel={() => { setEditing(null); setCreating(null); }}
              onSave={async (body) => {
                try {
                  if (editing) {
                    await upmodeAgentApi.updateAgent(editing.id, body);
                    toast.success("Agent updated");
                  } else {
                    await upmodeAgentApi.createAgent(body);
                    toast.success("Agent created");
                  }
                  setEditing(null); setCreating(null);
                  onChanged();
                } catch (e: any) {
                  toast.error(e.message || "Save failed");
                }
              }}
            />
          )}
        </div>
      </div>

      <ConfirmDialog
        open={agentPendingDelete !== null}
        onOpenChange={(open) => { if (!open) setAgentPendingDelete(null); }}
        onConfirm={confirmDelete}
        title={agentPendingDelete ? `Delete "${agentPendingDelete.name}"?` : "Delete agent?"}
        description={
          agentPendingDelete?.is_preset
            ? "This will remove your copy of this preset agent. You can always restore it by refreshing your presets."
            : "This will permanently delete this agent and it cannot be undone. Existing conversations with this agent will be preserved, but the agent itself will no longer be available for new chats."
        }
        confirmLabel="Delete"
        destructive
      />
    </div>
  );
}

// ─── Agent list row (unchanged except for reading publish_destinations) ──────

function AgentListRow({ agent, onEdit, onDelete }: {
  agent: UpmodeAgent;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const a = agent;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 11, border: "1px solid var(--border)", backgroundColor: "var(--muted)" }}>
      <div style={{ width: 34, height: 34, borderRadius: 9, backgroundColor: "var(--background)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
        {a.avatar_emoji || "🤖"}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--foreground)", overflowWrap: "anywhere", wordBreak: "break-word" }}>
            {a.name}
          </span>
          {a.is_preset && <MiniChip color="#7C3AED" bg="rgba(124,58,237,0.1)">Preset</MiniChip>}
          {a.brand_name && <MiniChip color="#0D9488" bg="rgba(13,148,136,0.08)">{a.brand_name}</MiniChip>}
          {!a.brand_name && !a.is_preset && <MiniChip color="#6B7280" bg="rgba(107,114,128,0.1)">All brands</MiniChip>}
        </div>
        <div style={{ fontSize: 11, color: "var(--muted-foreground)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {a.description}
        </div>
        <div style={{ display: "flex", gap: 5, marginTop: 5, flexWrap: "wrap" }}>
          <ModelChip model={a.model} />
          {a.tools_enabled.read_brand_data && <ToolChip icon={<Database size={9} />} label="data" />}
          {a.tools_enabled.send_email && <ToolChip icon={<Mail size={9} />} label="email" />}
          {a.tools_enabled.web_search && <ToolChip icon={<Globe size={9} />} label="web" />}
          {(a.publish_destinations || []).map(d => (
            <ToolChip key={d} icon={<Rocket size={9} />} label={d} />
          ))}
        </div>
      </div>
      <button onClick={onEdit} aria-label={`Edit ${a.name}`} style={iconBtn("#2563EB")} title="Edit">
        <Edit3 style={{ width: 12, height: 12 }} />
      </button>
      <button onClick={onDelete} aria-label={`Delete ${a.name}`} style={iconBtn("#EF4444")} title="Delete">
        <Trash2 style={{ width: 12, height: 12 }} />
      </button>
    </div>
  );
}

function MiniChip({ children, color, bg }: { children: React.ReactNode; color: string; bg: string }) {
  return (
    <span style={{ padding: "1px 7px", borderRadius: 999, backgroundColor: bg, color, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>
      {children}
    </span>
  );
}

function ModelChip({ model }: { model: string }) {
  return (
    <span style={{ padding: "1px 7px", borderRadius: 999, fontSize: 10, backgroundColor: "var(--background)", border: "1px solid var(--border)", color: "var(--muted-foreground)" }}>
      {model}
    </span>
  );
}

function ToolChip({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "1px 7px", borderRadius: 999, fontSize: 10, backgroundColor: "rgba(13,148,136,0.08)", color: "#0D9488", border: "1px solid rgba(13,148,136,0.2)" }}>
      {icon}
      <span style={{ fontWeight: 600 }}>{label}</span>
    </span>
  );
}

const iconBtn = (color: string): React.CSSProperties => ({
  padding: 7, borderRadius: 7, border: "1px solid var(--border)",
  backgroundColor: "var(--background)", cursor: "pointer",
  color, flexShrink: 0,
});

// ═══════════════════════════════════════════════════════════════════════════
//  Editor form
// ═══════════════════════════════════════════════════════════════════════════

function AgentEditor({
  initial, isEdit, activeBrandName, publishingIntegrations, loadingIntegrations, onCancel, onSave,
}: {
  initial: any;
  isEdit: boolean;
  activeBrandName?: string;
  publishingIntegrations: PublishingIntegration[];
  loadingIntegrations: boolean;
  onCancel: () => void;
  onSave: (body: any) => void;
}) {
  const [name, setName] = useState(initial.name || "");
  const [description, setDescription] = useState(initial.description || "");
  const [emoji, setEmoji] = useState(initial.avatar_emoji || "🤖");
  const [prompt, setPrompt] = useState(initial.system_prompt || "");
  const [model, setModel] = useState(initial.model || "claude-haiku-4-5");
  const [temperature, setTemperature] = useState(initial.temperature ?? 0.7);
  const [tools, setTools] = useState(initial.tools_enabled || { read_brand_data: true, send_email: false, web_search: false });
  const [examples, setExamples] = useState<ExampleConversation[]>(initial.example_conversations || []);

  // Publish destinations state
  const [publishDests, setPublishDests] = useState<PublishDestination[]>(
    Array.isArray(initial.publish_destinations) ? initial.publish_destinations : [],
  );

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const brandName: string | null = isEdit
    ? (initial.brand_name ?? null)
    : (initial.brand_name ?? activeBrandName ?? null);

  // Only `active` integrations are selectable. If the agent previously had
  // destinations configured that are no longer active (e.g. token revoked),
  // keep them in state so the user can see + uncheck them. But don't offer
  // them as newly selectable.
  const activeConnected = useMemo(
    () => publishingIntegrations.filter(i => i.status === "active"),
    [publishingIntegrations],
  );
  const activeConnectedSet = useMemo(
    () => new Set(activeConnected.map(i => i.destination)),
    [activeConnected],
  );

  const toggleDest = (d: PublishDestination) => {
    setPublishDests(prev =>
      prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d],
    );
  };

  const handleSave = async () => {
    const errs: string[] = [];
    if (!name.trim()) errs.push("Name is required");
    if (!prompt.trim() || prompt.trim().length < 10) errs.push("System prompt must be at least 10 characters");
    // Validate: agent can't be configured to publish to destinations not
    // currently connected. Soft check — backend enforces at tool-use time too.
    const invalidDests = publishDests.filter(d => !activeConnectedSet.has(d));
    if (invalidDests.length > 0) {
      errs.push(
        `These destinations are not currently connected: ${invalidDests.join(", ")}. ` +
        "Either remove them or connect them first in Integrations.",
      );
    }
    if (errs.length) { setErrors(errs); return; }
    setErrors([]);
    setSaving(true);
    await onSave({
      name: name.trim(),
      description: description.trim(),
      avatar_emoji: emoji,
      system_prompt: prompt,
      model,
      temperature,
      tools_enabled: tools,
      example_conversations: examples,
      brand_name: brandName,
      publish_destinations: publishDests,
    });
    setSaving(false);
  };

  // Whether to render the Publish section at all.
  // Per product decision: HIDE entirely when nothing is configured, so users
  // with zero publishing setups don't see it. Exception: if editing an agent
  // that already has destinations (e.g. from another brand), show them so
  // the user can clear them.
  const shouldShowPublishSection =
    activeConnected.length > 0 || publishDests.length > 0;

  return (
    <div className="p-4 md:p-5" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {errors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertTitle>Please fix the following before saving</AlertTitle>
          <AlertDescription>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Scope banner */}
      <div style={{
        padding: "9px 12px", borderRadius: 9,
        backgroundColor: "rgba(13,148,136,0.05)",
        border: "1px solid rgba(13,148,136,0.15)",
        fontSize: 11, color: "var(--foreground)", lineHeight: 1.5,
        display: "flex", alignItems: "flex-start", gap: 6,
        overflowWrap: "anywhere", wordBreak: "break-word",
      }}>
        <span style={{ flexShrink: 0 }}>{brandName ? "🎯" : "🌐"}</span>
        <span>
          {brandName
            ? <>This agent is scoped to <strong>{brandName}</strong> and won't appear when other brands are active.</>
            : <>This agent has no brand scope and will be visible across all brands.</>}
        </span>
      </div>

      {/* Identity */}
      <div style={{ display: "grid", gridTemplateColumns: "80px 1fr", gap: 10 }}>
        <div>
          <Label>Emoji</Label>
          <input value={emoji} onChange={e => setEmoji(e.target.value.slice(0, 4))}
            style={{ ...inputSt, textAlign: "center", fontSize: 22, padding: "6px 8px" }} />
        </div>
        <div style={{ minWidth: 0 }}>
          <Label>Name</Label>
          <input value={name} onChange={e => setName(e.target.value)}
            placeholder="e.g. Brand Analyst" style={inputSt} />
        </div>
      </div>

      <div>
        <Label>Description</Label>
        <input value={description} onChange={e => setDescription(e.target.value)}
          placeholder="What this agent does" style={inputSt} />
      </div>

      <div>
        <Label>System Prompt</Label>
        <textarea value={prompt} onChange={e => setPrompt(e.target.value)}
          placeholder="You are a helpful assistant that…" rows={7}
          style={{ ...inputSt, resize: "vertical", lineHeight: 1.55, fontFamily: "inherit" }} />
        <p style={hintSt}>Defines the agent's personality, tone, and behavior.</p>
      </div>

      {/* Model + Temperature */}
      <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-3">
        <div style={{ minWidth: 0 }}>
          <Label>Model</Label>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {MODEL_OPTIONS.map(opt => (
              <button key={opt.id} onClick={() => setModel(opt.id)}
                style={{ display: "flex", alignItems: "center", gap: 9, padding: "8px 11px", borderRadius: 8, border: `1px solid ${model === opt.id ? "#0D9488" : "var(--border)"}`, backgroundColor: model === opt.id ? "rgba(13,148,136,0.06)" : "var(--muted)", cursor: "pointer", textAlign: "left" }}>
                <div style={{ width: 13, height: 13, borderRadius: 7, border: `2px solid ${model === opt.id ? "#0D9488" : "var(--border)"}`, backgroundColor: model === opt.id ? "#0D9488" : "transparent", flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: model === opt.id ? "#0D9488" : "var(--foreground)" }}>{opt.label}</div>
                  <div style={{ fontSize: 10, color: "var(--muted-foreground)", marginTop: 1 }}>{opt.tag}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
        <div style={{ minWidth: 0 }}>
          <Label>Temperature: {temperature.toFixed(2)}</Label>
          <input type="range" min={0} max={1} step={0.05} value={temperature}
            onChange={e => setTemperature(parseFloat(e.target.value))}
            style={{ width: "100%" }} />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--muted-foreground)", marginTop: 3 }}>
            <span>Focused</span>
            <span>Creative</span>
          </div>
        </div>
      </div>

      {/* Tools */}
      <div>
        <Label><Wrench style={{ width: 10, height: 10, display: "inline", marginRight: 4, verticalAlign: "middle" }} />Tools & Capabilities</Label>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <ToolToggle
            icon={<Database size={13} />}
            label="Read brand data"
            desc="Access brand monitoring, cognition, fix actions, simulations, prompt tracking, and workflow results."
            enabled={tools.read_brand_data}
            onChange={v => setTools({ ...tools, read_brand_data: v })}
          />
          <ToolToggle
            icon={<Mail size={13} />}
            label="Send email (with confirmation)"
            desc="Draft and send emails. Every send requires explicit user approval in the UI."
            enabled={tools.send_email}
            onChange={v => setTools({ ...tools, send_email: v })}
          />
          <ToolToggle
            icon={<Globe size={13} />}
            label="Web search"
            desc="Reason over both your Upmode data AND the live web. Upmode data is preferred for brand questions."
            enabled={tools.web_search}
            onChange={v => setTools({ ...tools, web_search: v })}
          />
        </div>
      </div>

      {/* Publish destinations.
          Rendered when: (a) there's at least one active publishing integration
          for this brand, OR (b) the agent being edited already has some
          destinations configured (even if they're no longer connected).
          Otherwise show a small empty-state hint — silently hiding the
          section makes it look broken when it isn't. */}
      {shouldShowPublishSection ? (
        <div>
          <Label>
            <Rocket style={{ width: 10, height: 10, display: "inline", marginRight: 4, verticalAlign: "middle" }} />
            Publish to destinations
          </Label>
          <div style={{
            padding: "8px 11px", borderRadius: 9,
            backgroundColor: "rgba(13,148,136,0.04)",
            border: "1px solid rgba(13,148,136,0.12)",
            fontSize: 11, color: "var(--muted-foreground)",
            marginBottom: 8, lineHeight: 1.5,
          }}>
            Let this agent draft content for the destinations below. User always confirms before anything is published.
            {brandName && <> Uses integrations configured for <strong>{brandName}</strong>.</>}
          </div>
          {loadingIntegrations ? (
            <div style={{ fontSize: 11, color: "var(--muted-foreground)" }}>Loading destinations…</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {activeConnected.map(integ => {
                const dest = integ.destination as PublishDestination;
                const meta = DEST_META[dest];
                const checked = publishDests.includes(dest);
                const acct = integ.account_metadata || {};
                const acctLabel =
                  acct.publication_title || acct.site_url || acct.author_name ||
                  (dest === "linkedin" ? "Connected" : "");

                return (
                  <DestToggle
                    key={dest}
                    icon={<meta.Icon style={{ width: 13, height: 13, color: meta.color }} />}
                    label={meta.label}
                    desc={acctLabel ? `Connected · ${acctLabel}` : "Connected"}
                    enabled={checked}
                    onChange={() => toggleDest(dest)}
                  />
                );
              })}

              {/* Show destinations that were configured previously but are no
                  longer active (reconnect needed), so the user can see + unset. */}
              {publishDests
                .filter(d => !activeConnectedSet.has(d))
                .map(d => {
                  const meta = DEST_META[d];
                  return (
                    <DestToggle
                      key={d}
                      icon={<meta.Icon style={{ width: 13, height: 13, color: meta.color }} />}
                      label={meta.label}
                      desc="Not currently connected — reconnect in Integrations, or uncheck to remove."
                      enabled={true}
                      onChange={() => toggleDest(d)}
                      warn
                    />
                  );
                })}
            </div>
          )}
        </div>
      ) : (
        // Empty state — explain WHY the section has nothing in it, so the
        // user doesn't think the feature is broken when it's actually just
        // unconfigured.
        <div>
          <Label>
            <Rocket style={{ width: 10, height: 10, display: "inline", marginRight: 4, verticalAlign: "middle" }} />
            Publish to destinations
          </Label>
          <div style={{
            padding: "10px 12px", borderRadius: 9,
            border: "1px dashed var(--border)",
            backgroundColor: "var(--muted)",
            fontSize: 11, color: "var(--muted-foreground)",
            lineHeight: 1.55,
          }}>
            {loadingIntegrations
              ? <>Loading destinations…</>
              : brandName
                ? <>
                    No publishing destinations are configured for{" "}
                    <strong>{brandName}</strong> yet. Go to{" "}
                    <strong>Integrations → Publishing</strong> to connect Hashnode,
                    WordPress, or LinkedIn. Once connected, come back and edit this
                    agent to enable publishing.
                  </>
                : <>
                    No active brand is selected, so publishing destinations aren't
                    available. Select a brand first, then configure publishing
                    destinations on the Integrations page.
                  </>
            }
          </div>
        </div>
      )}

      <ExampleConversationsEditor examples={examples} onChange={setExamples} />

      <div style={{
        display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap",
        paddingTop: 12, borderTop: "1px solid var(--border)", marginTop: 6,
      }}>
        <button onClick={onCancel} disabled={saving}
          style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid var(--border)", backgroundColor: "var(--muted)", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "var(--foreground)" }}>
          Cancel
        </button>
        <button onClick={handleSave} disabled={saving}
          style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 18px", borderRadius: 8, border: "none", backgroundColor: saving ? "var(--border)" : "#0D9488", color: "white", cursor: saving ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 600 }}>
          <Save style={{ width: 12, height: 12 }} />
          {saving ? "Saving…" : (isEdit ? "Save Changes" : "Create Agent")}
        </button>
      </div>
    </div>
  );
}

// ─── Small components (same pattern as existing ToolToggle) ────────────────

function Label({ children }: { children: React.ReactNode }) {
  return <p style={{ fontSize: 10, fontWeight: 700, color: "var(--muted-foreground)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5, margin: "0 0 5px 0" }}>{children}</p>;
}

const inputSt: React.CSSProperties = {
  width: "100%", padding: "8px 11px", borderRadius: 8,
  border: "1px solid var(--border)", backgroundColor: "var(--muted)",
  fontSize: 12, color: "var(--foreground)",
  outline: "none", boxSizing: "border-box",
};
const hintSt: React.CSSProperties = { fontSize: 11, color: "var(--muted-foreground)", marginTop: 4, margin: "4px 0 0 0" };

function ToolToggle({ icon, label, desc, enabled, onChange, disabled }: {
  icon: React.ReactNode; label: string; desc: string;
  enabled: boolean; onChange: (v: boolean) => void; disabled?: boolean;
}) {
  return (
    <button
      onClick={() => !disabled && onChange(!enabled)}
      disabled={disabled}
      style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 11px", borderRadius: 9, border: `1px solid ${enabled && !disabled ? "#0D9488" : "var(--border)"}`, backgroundColor: enabled && !disabled ? "rgba(13,148,136,0.05)" : "var(--muted)", cursor: disabled ? "not-allowed" : "pointer", textAlign: "left", opacity: disabled ? 0.5 : 1 }}
    >
      <div style={{ width: 28, height: 28, borderRadius: 7, backgroundColor: enabled ? "rgba(13,148,136,0.12)" : "var(--background)", border: `1px solid ${enabled ? "rgba(13,148,136,0.25)" : "var(--border)"}`, display: "flex", alignItems: "center", justifyContent: "center", color: enabled ? "#0D9488" : "var(--muted-foreground)", flexShrink: 0 }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--foreground)" }}>{label}</div>
        <div style={{ fontSize: 10, color: "var(--muted-foreground)", marginTop: 1, overflowWrap: "anywhere", wordBreak: "break-word" }}>{desc}</div>
      </div>
      <div style={{ width: 32, height: 18, borderRadius: 999, backgroundColor: enabled && !disabled ? "#0D9488" : "var(--border)", position: "relative", transition: "background 0.15s", flexShrink: 0 }}>
        <div style={{ position: "absolute", top: 2, left: enabled && !disabled ? 16 : 2, width: 14, height: 14, borderRadius: 999, backgroundColor: "white", transition: "left 0.15s", boxShadow: "0 1px 2px rgba(0,0,0,0.2)" }} />
      </div>
    </button>
  );
}

// Destination toggle — visually lighter than ToolToggle, uses destination color
function DestToggle({ icon, label, desc, enabled, onChange, warn }: {
  icon: React.ReactNode; label: string; desc: string;
  enabled: boolean; onChange: () => void; warn?: boolean;
}) {
  const borderColor = warn
    ? "rgba(245,158,11,0.4)"
    : enabled ? "rgba(13,148,136,0.4)" : "var(--border)";
  const bg = warn
    ? "rgba(245,158,11,0.04)"
    : enabled ? "rgba(13,148,136,0.04)" : "var(--muted)";
  return (
    <button
      onClick={onChange}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "8px 11px", borderRadius: 9,
        border: `1px solid ${borderColor}`,
        backgroundColor: bg,
        cursor: "pointer", textAlign: "left",
      }}
    >
      <div style={{
        width: 16, height: 16, borderRadius: 4,
        border: `2px solid ${enabled ? "#0D9488" : "var(--border)"}`,
        backgroundColor: enabled ? "#0D9488" : "transparent",
        flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {enabled && (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
      <div style={{ width: 24, height: 24, borderRadius: 6, backgroundColor: "var(--background)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--foreground)" }}>{label}</div>
        <div style={{ fontSize: 10, color: warn ? "#B45309" : "var(--muted-foreground)", marginTop: 1, overflowWrap: "anywhere", wordBreak: "break-word" }}>
          {desc}
        </div>
      </div>
    </button>
  );
}

function ExampleConversationsEditor({
  examples, onChange,
}: {
  examples: ExampleConversation[];
  onChange: (next: ExampleConversation[]) => void;
}) {
  const addExample = () => onChange([...examples, { title: "", turns: [{ role: "user", content: "" }, { role: "assistant", content: "" }] }]);
  const removeExample = (i: number) => onChange(examples.filter((_, idx) => idx !== i));
  const updateExample = (i: number, patch: Partial<ExampleConversation>) => {
    const copy = examples.slice();
    copy[i] = { ...copy[i], ...patch };
    onChange(copy);
  };
  const updateTurn = (exIdx: number, turnIdx: number, content: string) => {
    const copy = examples.slice();
    copy[exIdx].turns = copy[exIdx].turns.slice();
    copy[exIdx].turns[turnIdx] = { ...copy[exIdx].turns[turnIdx], content };
    onChange(copy);
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, gap: 8, flexWrap: "wrap" }}>
        <Label><Sparkles style={{ width: 10, height: 10, display: "inline", marginRight: 4, verticalAlign: "middle" }} />Example Conversations (few-shot)</Label>
        <button onClick={addExample}
          style={{ display: "flex", alignItems: "center", gap: 3, padding: "4px 9px", borderRadius: 6, border: "1px solid var(--border)", backgroundColor: "var(--muted)", cursor: "pointer", fontSize: 11, color: "#0D9488", fontWeight: 600, flexShrink: 0 }}>
          <Plus style={{ width: 10, height: 10 }} />
          Add example
        </button>
      </div>

      {examples.length === 0 && (
        <div style={{ padding: "10px 11px", borderRadius: 8, border: "1px dashed var(--border)", backgroundColor: "var(--muted)", fontSize: 11, color: "var(--muted-foreground)", textAlign: "center" }}>
          Optional: add sample user/assistant turns to guide the agent's style.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {examples.map((ex, i) => (
          <div key={i} style={{ padding: 11, borderRadius: 9, border: "1px solid var(--border)", backgroundColor: "var(--muted)" }}>
            <div style={{ display: "flex", gap: 7, marginBottom: 8 }}>
              <input value={ex.title} onChange={e => updateExample(i, { title: e.target.value })}
                placeholder={`Example ${i + 1} title`}
                style={{ ...inputSt, backgroundColor: "var(--background)", fontWeight: 600 }} />
              <button onClick={() => removeExample(i)} aria-label="Remove example"
                style={{ padding: 7, borderRadius: 6, border: "1px solid var(--border)", backgroundColor: "var(--background)", cursor: "pointer", color: "#EF4444", flexShrink: 0 }}
                title="Remove">
                <Trash2 style={{ width: 11, height: 11 }} />
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {ex.turns.map((turn, ti) => (
                <div key={ti} style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: turn.role === "user" ? "#2563EB" : "#0D9488", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>
                    {turn.role}
                  </div>
                  <textarea value={turn.content} onChange={e => updateTurn(i, ti, e.target.value)}
                    placeholder={turn.role === "user" ? "What the user might ask…" : "How the agent should respond…"}
                    rows={2}
                    style={{ ...inputSt, backgroundColor: "var(--background)", fontFamily: "inherit", lineHeight: 1.5, resize: "vertical" }} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}